import { inject, Injectable } from '@angular/core'

import { BehaviorSubject, Observable, of, takeUntil, tap, withLatestFrom } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

import { Vehicle } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { TimelineItem, TripHistoryItem } from '@ti/core/api/scopes/fleet/routes/vehicles.route'
import { DateRange } from '@ti/core/common/contracts'
import {
  completeStore,
  exposeReadonlyState,
  injectDestroy$,
  toSignalState,
} from '@ti/core/common/utils'

import { UITripHistoryData } from '../contracts'

@Injectable()
// TODO: Format UiTripData inside the model
// TODO: Store the trip ID in store$->selectedTrip
export class TripsListModel {
  protected readonly api = inject(ApiService)
  protected readonly destroy$ = injectDestroy$()

  protected readonly MAX_SAFE_LIMIT = 100

  protected readonly store$ = {
    areTripsLoading: new BehaviorSubject<boolean>(true),
    canLoadMoreTrips: new BehaviorSubject<boolean>(true),
    currentVehicle: new BehaviorSubject<Vehicle | undefined>(undefined),
    dateRange: new BehaviorSubject<DateRange | undefined>(undefined),
    loadNextPage: new BehaviorSubject<void>(undefined),
    loadedTrips: new BehaviorSubject<TripHistoryItem[]>([]),
    hoveredTripId: new BehaviorSubject<number | null>(null),
    selectedTrip: new BehaviorSubject<UITripHistoryData | null>(null), // TODO: Store trip id
    pageSize: new BehaviorSubject<number>(10),
  }

  public readonly state$ = {
    ...exposeReadonlyState(this.store$, [
      'areTripsLoading',
      'canLoadMoreTrips',
      'currentVehicle',
      'dateRange',
      'loadedTrips',
      'selectedTrip',
      'hoveredTripId',
    ]),
  }

  public readonly state = toSignalState(this.state$)

  public constructor() {
    Promise.resolve()
      .then(() => this.init())
      .catch((error) => console.warn(`Cannot initialize TripsListModel`, error))

    this.destroy$.subscribe(() => completeStore(this.store$))
  }

  public setVehicle(vehicle?: Vehicle) {
    this.store$.loadedTrips.next([])
    this.store$.currentVehicle.next(vehicle)
    if (vehicle) {
      this.loadTripsNextPage()
    }
  }

  public setDateRange(range?: DateRange) {
    this.store$.dateRange.next(range)
    this.store$.loadedTrips.next([])
    return this.loadTripsNextPage()
  }

  public selectTrip(trip?: UITripHistoryData) {
    const selected = this.store$.selectedTrip.getValue()
    if (!trip || trip?.id === selected?.id) {
      if (selected !== null) {
        this.store$.selectedTrip.next(null)
      }
    } else {
      this.store$.selectedTrip.next(trip)
    }
  }

  public setHoveredTripId(tripId?: number) {
    this.store$.hoveredTripId.next(tripId || null)
  }

  public loadTripsNextPage() {
    this.store$.loadNextPage.next()
  }

  public setPageSize(pageSize: number) {
    this.store$.pageSize.next(pageSize)
  }

  protected init() {
    this.store$.loadNextPage
      .pipe(
        withLatestFrom(
          this.store$.loadedTrips,
          this.store$.currentVehicle,
          this.store$.dateRange,
          this.store$.pageSize,
        ),
        tap(() => this.store$.areTripsLoading.next(true)),
        switchMap(([, loadedTrips, currentVehicle, dateRange, pageSize]) => {
          if (!currentVehicle) return of([])

          // Load all trips when specific date range is provided
          const limit = dateRange ? this.MAX_SAFE_LIMIT : pageSize
          const timestampTo = loadedTrips.length
            ? loadedTrips[loadedTrips.length - 1].begin
            : Math.round((dateRange ? dateRange[1].valueOf() : Date.now()) / 1000)
          const timestampFrom = dateRange ? Math.round(dateRange[0].valueOf() / 1000) : undefined

          return this.loadVehicleTimeline$(currentVehicle.primaryExternalDeviceId, {
            limit,
            timestampTo,
            timestampFrom,
          }).pipe(
            map((nextTrips) => {
              this.store$.canLoadMoreTrips.next(nextTrips.length === pageSize)
              const tripIds = loadedTrips.map((t) => t.id)
              return [...loadedTrips, ...nextTrips.filter((t) => !tripIds.includes(t.id))]
            }),
          )
        }),
        tap(() => this.store$.areTripsLoading.next(false)),
        takeUntil(this.destroy$),
      )
      .subscribe((totalTrips) => this.store$.loadedTrips.next(totalTrips))
  }

  protected loadVehicleTimeline$(
    deviceId: string | number,
    options: { limit: number; timestampTo: number; timestampFrom?: number },
  ): Observable<TimelineItem[]> {
    return this.api.fleet.vehicle.timeline$(deviceId, options)
  }
}
